import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import Image from 'components/Common/Image'
import NestableClickable from 'components/Common/NestableClickable'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import { isHotelSavedData } from 'tripPlanner/utils/savedItemDataGuards'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Caption from 'LuxKit/Typography/Caption'
import TextButton from 'LuxKit/Button/TextButton'
import Divider from 'LuxKit/Divider'
import PriceRowPriceWithCaption from 'LuxKit/PricePoints/PriceRowPriceWithCaption'
import PriceDetails from 'tripPlanner/components/ItemOfferPrice/PriceDetails'
import ExperienceTilePrices from 'components/Experiences/ExperienceTiles/ExperienceTilePrices'
import noop from 'lib/function/noop'
import CurrencyContext, { CurrencyProvider } from 'contexts/currencyContext'
import ChatItemDropdown from './ChatItemDropdown'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import { STOP_VISIT_ROLE } from 'tripPlanner/api/types'
import type { TripItineraryItem, TripItemWithoutDay, StopVisitRole } from 'tripPlanner/api/types'
import GooglePlacePriceLevel, { shouldShowPriceLevel } from 'tripPlanner/components/GooglePlacePriceLevel'
import { formatShortDateRange } from 'tripPlanner/utils'
import { SHORT_TIME_HOUR_FROMAT_AM_PM_SPACE } from 'constants/dateFormats'

const TileContainer = styled(NestableClickable)`
  min-height: ${rem(120)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.L};
  display: flex;
  position: relative;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  cursor: pointer;
  text-align: left;

  &:hover {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`

const TileImage = styled.div`
  width: var(--itinerary-item-image-width, ${rem(179)});
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
`

const StyledImage = styled(Image)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const ContentArea = styled.div`
  flex: 1;
  padding: var(--itinerary-item-card-padding, ${rem(16)});
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  min-width: 0;
`

const EndAligned = styled.div`
  margin-left: auto;
`

const CondensedContainer = styled(NestableClickable)`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.M};
  padding: ${rem(12)} var(--itinerary-item-card-padding, ${rem(16)});
  width: 100%;
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  cursor: pointer;
  text-align: left;

  &:hover {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`

interface Props {
  item: TripItineraryItem | TripItemWithoutDay
  /** StopVisitRole from the day context. Only present for day items. */
  role?: StopVisitRole
  /** True when this is the first day of an accommodation stay. */
  isCheckIn?: boolean
  /** 1-indexed day number within the stay. */
  dayOfStay?: number
  /** Total number of days in the stay. */
  totalDays?: number
  /** Pre-computed date range string for hotel stays (e.g., "24-26 April"). */
  stayDateRange?: string
  onDelete?: (itemId: number) => void
  onClick?: (googlePlaceId: string, placeName: string, itemId: number) => void
  onViewOfferDetails?: (item: TripItineraryItem | TripItemWithoutDay) => void
  onAddToTrip?: (item: TripItineraryItem | TripItemWithoutDay) => void
}

function ItineraryItemCard(props: Props) {
  const intl = useIntl()
  const currentCurrency = useContext(CurrencyContext)
  const {
    item,
    role,
    isCheckIn,
    dayOfStay,
    totalDays,
    stayDateRange,
    onDelete = noop,
    onClick = noop,
    onViewOfferDetails,
    onAddToTrip,
  } = props

  const dayOfStayLabel = intl.formatMessage(
    {
      id: 'YosgkXj7pPr-u_rXMbxk',
      defaultMessage: 'Day {dayOfStay} of {totalDays}',
      description: 'Caption showing the current day number within a hotel stay duration on the itinerary item card',
    },
    { dayOfStay, totalDays },
  )

  const viewDetailsLabel = intl.formatMessage({
    id: 'pTz2_TivJ1kKG7FhpOcS',
    defaultMessage: 'View details',
    description: 'Button label to view hotel details on the itinerary item card',
  })

  const bookingDetailsLabel = intl.formatMessage({
    id: 'V3if7yCh9Y75j9TcYdbF',
    defaultMessage: 'Booking details',
    description: 'CTA button text to view booking details in trip planner',
  })

  const savedItemData = item.savedItemData
  const offerData = isHotelSavedData(savedItemData) ? savedItemData : undefined
  const experienceData = savedItemData?.source === 'offer_experience' ? savedItemData : undefined
  const itemImageIds = savedItemData && 'imageIds' in savedItemData ? savedItemData.imageIds : undefined
  const isAccommodation = item.type === 'ACCOMMODATION'
  const isOngoingStay = isAccommodation && role === STOP_VISIT_ROLE.STAY && isCheckIn === false
  const hasHotelDetails = Boolean(offerData?.imageIds?.length)
  const hasExperienceDetails = Boolean(experienceData)
  const isBooked = 'isBooked' in item && item.isBooked
  const confirmationCode = 'confirmationCode' in item && typeof item.confirmationCode === 'string' ? item.confirmationCode : undefined
  const orderUrl = confirmationCode ? `/account/my-escapes/order/${confirmationCode}` : '/account/my-escapes'

  const firstImageId = useMemo(() => {
    if (itemImageIds?.length) return itemImageIds[0]
    return item.itemPlaceDetail?.imageIds?.[0]
  }, [itemImageIds, item.itemPlaceDetail?.imageIds])

  const googlePlaceId = item.itemPlaceDetail?.placeId

  const handleDelete = useCallback(() => {
    onDelete(item.id)
  }, [item.id, onDelete])

  const handleClick = useCallback(() => {
    if (isAccommodation && hasHotelDetails && onViewOfferDetails) {
      onViewOfferDetails(item)
      return
    }
    if (hasExperienceDetails && onViewOfferDetails) {
      onViewOfferDetails(item)
      return
    }
    if (googlePlaceId) {
      onClick(googlePlaceId, item.name, item.id)
    }
  }, [isAccommodation, hasHotelDetails, hasExperienceDetails, onViewOfferDetails, item, googlePlaceId, onClick])

  const handleViewDetails = useCallback(() => {
    onViewOfferDetails?.(item)
  }, [onViewOfferDetails, item])

  const handleAddToTrip = useCallback(() => {
    onAddToTrip?.(item)
  }, [onAddToTrip, item])

  const addToTripHandler = isAccommodation && onAddToTrip ? handleAddToTrip : undefined

  const dateRangeText = useMemo(() => {
    if (!offerData?.checkIn || !offerData?.checkOut) return null
    return formatShortDateRange({
      startDate: moment(offerData.checkIn),
      endDate: moment(offerData.checkOut),
      includeYear: false,
    })
  }, [offerData?.checkIn, offerData?.checkOut])

  const resolvedDateText = dateRangeText ?? stayDateRange ?? null

  const checkInTime = useMemo(() => {
    if (!('startTime' in item) || !item.startTime) return null
    const startTime = moment(item.startTime)
    if (startTime.hours() === 0 && startTime.minutes() === 0) {
      return null
    }
    return startTime.format(SHORT_TIME_HOUR_FROMAT_AM_PM_SPACE)
  }, [item])

  const price = offerData?.price ?? experienceData?.price
  const priceCurrency = offerData?.currencyCode ?? experienceData?.currencyCode
  const experienceMemberPrice = experienceData?.memberPrice
  const showAccommodationFooter = !!price || isBooked || (onViewOfferDetails && hasHotelDetails)

  const luxPlusPrice = offerData?.memberPrice

  const fromLabel = intl.formatMessage({
    id: 'lrN1MpB7FBREq1ybic9U',
    defaultMessage: 'From',
    description: 'Price caption prefix meaning "starting from" on itinerary item card',
  })

  if (isOngoingStay) {
    const stayCaption = dayOfStay !== undefined && totalDays !== undefined
      ? dayOfStayLabel
      : stayDateRange

    let endActions: React.ReactNode
    if (isBooked) {
      endActions = (
        <TextButton size="small" kind="tertiary" href={orderUrl} target="_blank" rel="noopener noreferrer">
          {bookingDetailsLabel}
        </TextButton>
      )
    } else {
      endActions = (
        <Group direction="horizontal" gap={8} verticalAlign="center">
          {onViewOfferDetails && hasHotelDetails && (
            <TextButton size="small" kind="tertiary" onClick={handleViewDetails}>
              {viewDetailsLabel}
            </TextButton>
          )}
          <ChatItemDropdown onDelete={handleDelete} onAddToTrip={addToTripHandler} />
        </Group>
      )
    }

    return (
      <CondensedContainer onClick={handleClick}>
        <Group direction="horizontal" gap={12} verticalAlign="center" horizontalAlign="space-between" fullWidth>
          <Group direction="horizontal" gap={12} verticalAlign="center">
            <LineBedIcon size="S" />
            <BodyText variant="medium" weight="bold">{item.name}</BodyText>
            {stayCaption && (
              <Caption variant="large" colour="light-foreground-alt">
                {stayCaption}
              </Caption>
            )}
          </Group>
          {endActions}
        </Group>
      </CondensedContainer>
    )
  }

  if (isAccommodation) {
    return (
      <TileContainer onClick={handleClick}>
        {firstImageId && (
          <TileImage>
            <StyledImage id={firstImageId} fit="center" />
          </TileImage>
        )}
        <ContentArea>
          <Group direction="vertical" gap={8} fullWidth>
            <Group direction="vertical" fullWidth>
              <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" fullWidth>
                <Heading variant="heading6">{item.name}</Heading>
                <ChatItemDropdown onDelete={handleDelete} onAddToTrip={addToTripHandler} />
              </Group>

              <Group direction="horizontal" gap={8} verticalAlign="center" wrap="wrap" noDisplayWhenEmpty>
                {offerData?.rating !== undefined && offerData.rating > 0 && (
                  <NumberRating
                    rating={offerData.rating}
                    variant="xs"
                    hideSubtitle
                    inlineLabel
                  />
                )}

                {resolvedDateText && (
                  <>
                    {offerData?.rating && <Divider kind="secondary" direction="vertical" />}
                    <Caption variant="large" colour="light-foreground-alt">{resolvedDateText}</Caption>
                  </>
                )}

                {checkInTime && (
                  <>
                    {(offerData?.rating || resolvedDateText) && <Divider kind="secondary" direction="vertical" />}
                    <Caption variant="large" colour="light-foreground-alt">{checkInTime}</Caption>
                  </>
                )}
              </Group>
            </Group>
            {item.description && (
              <BodyText variant="medium" colour="light-foreground-alt">
                {item.description}
              </BodyText>
            )}

          </Group>

          {showAccommodationFooter && (
            <Group direction="horizontal" gap={16} verticalAlign="end" wrap="wrap" fullWidth>
              {!isBooked && price && (
                <PriceDetails
                  price={price}
                  luxPlusPrice={luxPlusPrice}
                  currencyCode={offerData?.currencyCode ?? currentCurrency}
                  priceLabel={fromLabel}
                  totalLabel={offerData?.saleUnit ?? 'total'}
                  showTaxesAndFees={false}
                />
              )}
              {onViewOfferDetails && hasHotelDetails && (
                <EndAligned>
                  <TextButton size="medium" kind="primary" onClick={handleViewDetails}>
                    {isBooked ? bookingDetailsLabel : viewDetailsLabel}
                  </TextButton>
                </EndAligned>
              )}
            </Group>
          )}
        </ContentArea>
      </TileContainer>
    )
  }

  const placeDetail = item.itemPlaceDetail
  const placeCategory = placeDetail?.primaryTypeDisplayName
  const priceLevel = placeDetail?.priceLevel
  const showPriceLevel = shouldShowPriceLevel(priceLevel)
  const hasOpeningHours = Boolean(placeDetail?.openingHours?.length)
  const hasPlaceMetadata = Boolean(placeCategory || showPriceLevel)

  const bookViaVenueLabel = intl.formatMessage({
    id: '1ZES2H8MvVxJQp4nvtjW',
    defaultMessage: 'Booking may be required — check venue details',
    description: 'Label shown on itinerary item tile when the venue has opening hours and can be booked directly',
  })

  return (
    <TileContainer onClick={handleClick}>
      {firstImageId && (
        <TileImage>
          <StyledImage id={firstImageId} fit="center" />
        </TileImage>
      )}
      <ContentArea>
        <Group direction="vertical" gap={8} fullWidth>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" fullWidth>
            <Group direction="vertical" gap={2}>
              <Heading variant="heading6">{item.name}</Heading>
              {hasPlaceMetadata && (
                <Group direction="horizontal" gap={8} verticalAlign="center" wrap="wrap" noDisplayWhenEmpty>
                  {placeCategory && <Caption variant="large" colour="light-foreground-alt">{placeCategory}</Caption>}
                  {showPriceLevel && placeCategory && <Divider kind="primary" direction="vertical" />}
                  {showPriceLevel && <GooglePlacePriceLevel priceLevel={priceLevel} size="small" colour="light-foreground-alt" />}
                </Group>
              )}
            </Group>
            <ChatItemDropdown onDelete={handleDelete} />
          </Group>
          {item.description && <BodyText variant="medium">{item.description}</BodyText>}
          {hasOpeningHours && (
            <BodyTextBlock variant="small" startIcon={<LineCalendarIcon />} weight="bold">
              {bookViaVenueLabel}
            </BodyTextBlock>
          )}
        </Group>
        {(isBooked || price || (onViewOfferDetails && hasExperienceDetails)) && (
          <Group direction="horizontal" gap={16} verticalAlign="end" wrap="wrap" fullWidth>
            {price && experienceData && (
              <CurrencyProvider currency={priceCurrency}>
                <Group direction="vertical" gap={4}>
                  <ExperienceTilePrices
                    totalPrice={price}
                    totalMemberPrice={experienceMemberPrice}
                    totalGrossPrice={experienceData?.value}
                    discountPercentage={experienceData?.discountPercent}
                    discountMemberPercentage={experienceData?.memberDiscountPercent}
                    isFromPrice
                    ticketUnitLabel="person"
                    size="S"
                  />
                </Group>
              </CurrencyProvider>
            )}
            {price && !experienceData && (
              <CurrencyProvider currency={offerData?.currencyCode}>
                <PriceRowPriceWithCaption
                  size="S"
                  price={price}
                  caption={intl.formatMessage({
                    id: 'lrN1MpB7FBREq1ybic9U',
                    defaultMessage: 'From',
                    description: 'Price caption prefix meaning "starting from" on itinerary item card',
                  })}
                  saleUnit={offerData?.saleUnit ?? 'total'}
                />
              </CurrencyProvider>
            )}
            {isBooked && (
              <EndAligned>
                <TextButton size="medium" kind="primary" href={orderUrl} target="_blank" rel="noopener noreferrer">
                  {bookingDetailsLabel}
                </TextButton>
              </EndAligned>
            )}
            {!isBooked && onViewOfferDetails && hasExperienceDetails && (
              <EndAligned>
                <TextButton size="medium" kind="primary" onClick={handleViewDetails}>
                  {viewDetailsLabel}
                </TextButton>
              </EndAligned>
            )}
          </Group>
        )}
      </ContentArea>
    </TileContainer>
  )
}

export default ItineraryItemCard
